import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'

export interface SampleW9 {
    id: number
    apartment: number
    apartment_name: string
    apartment_ids?: number[]
    apartment_names?: string[]
    file: string
    name: string
    ai_summary: string | null
    uploaded_by: number
    created_date: string
    modified_date: string
}

export interface OrgSampleW9Params {
    organization?: number | null
}

const QUERY_KEY = ['orgSampleW9s']

export const useOrgSampleW9s = (params?: OrgSampleW9Params) => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    // Build query string for list endpoints
    const buildQueryString = () => {
        if (!params?.organization) return ''
        const searchParams = new URLSearchParams()
        searchParams.append('organization', params.organization.toString())
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    // Build query string for detail mutations (organization only)
    const buildDetailQueryString = () => {
        if (!params?.organization) return ''
        return `?organization=${params.organization}`
    }

    // Get all sample W-9s for organization
    const {
        data: sampleW9s,
        isLoading: isLoadingSampleW9s,
        error: errorSampleW9s,
        refetch: refetchSampleW9s,
    } = useQuery<AxiosResponse<SampleW9[]>, Error>({
        queryKey: [...QUERY_KEY, params?.organization],
        queryFn: () =>
            axiosInstance.get(`/company/sample_w9/${buildQueryString()}`),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    const processSampleW9 = useMutation({
        mutationFn: async (id: number) => {
            setProcessingIds((prev) => new Set(prev).add(id))
            return axiosInstance.post(
                `/company/sample_w9/${id}/process_w9/${buildDetailQueryString()}`,
            )
        },
        onSuccess: (_, id) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Sample W-9 analyzed successfully')
        },
        onError: (error: any, id) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            toast.error(error?.response?.data?.error || 'Failed to analyze W-9')
        },
    })

    const createSampleW9 = useMutation({
        mutationFn: async ({
            file,
            apartment_ids,
        }: {
            file: File
            apartment_ids: number[]
        }) => {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('apartment_ids', JSON.stringify(apartment_ids))
            return axiosInstance.post<SampleW9>(
                `/company/sample_w9/${buildQueryString()}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ['sampleW9s'] })
            toast.success('Sample W-9 uploaded successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload sample W-9'
            toast.error(errorMessage)
        },
    })

    const uploadSampleW9s = async (files: File[], apartmentIds: number[]) => {
        return Promise.allSettled(
            files.map((file) =>
                createSampleW9.mutateAsync({
                    file,
                    apartment_ids: apartmentIds,
                }),
            ),
        )
    }

    const updateSampleW9 = useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: number
            data: { apartment_ids: number[] }
        }) => {
            return axiosInstance.patch<SampleW9>(
                `/company/sample_w9/${id}/${buildDetailQueryString()}`,
                data,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ['sampleW9s'] })
            toast.success('Sample W-9 updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update sample W-9'
            toast.error(errorMessage)
        },
    })

    // Delete a sample W-9
    const deleteSampleW9 = useMutation({
        mutationFn: async (id: number) => {
            return axiosInstance.delete(
                `/company/sample_w9/${id}/${buildDetailQueryString()}`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Sample W-9 deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete sample W-9'
            toast.error(errorMessage)
        },
    })

    const sendToVendors = useMutation({
        mutationFn: async ({
            id,
            vendorIds,
        }: {
            id: number
            vendorIds: number[]
        }) => {
            return axiosInstance.post(
                `/company/sample_w9/${id}/send_to_vendors/${buildDetailQueryString()}`,
                { vendor_ids: vendorIds },
            )
        },
        onSuccess: (response) => {
            const data = response.data || {}
            const sentCount = data.sent?.length || 0
            const skippedCount = data.skipped?.length || 0
            if (sentCount > 0) {
                toast.success(
                    `Sample W-9 sent to ${sentCount} vendor(s)${
                        skippedCount > 0
                            ? ` (${skippedCount} skipped — no email)`
                            : ''
                    }`,
                )
            } else {
                toast.warning(
                    'No vendors were emailed. Add an email address and try again.',
                )
            }
        },
        onError: (error: any) => {
            toast.error(
                error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    'Failed to send W-9 to vendors',
            )
        },
    })

    return {
        sampleW9s: sampleW9s?.data || [],
        isLoadingSampleW9s,
        errorSampleW9s,
        refetchSampleW9s,
        deleteSampleW9,
        updateSampleW9,
        uploadSampleW9s,
        processSampleW9,
        sendToVendors,
        processingIds,
        isSending: sendToVendors.isLoading,
        isDeleting: deleteSampleW9.isLoading,
        isUpdating: updateSampleW9.isLoading,
        isUploading: createSampleW9.isLoading,
    }
}
